import React, { useState } from 'react';
import { Column, Row } from 'react-foundation';
import * as ContactsAPI from '../../../../api/ContactsAPI';
import { SponsorshipPartner } from '../SponsorshipPartner';
import PurpleLoadingSpinner from '../../../../../assets/images/loading-spinner-purple.svg';
/**
 * The overview tab for organization profiles
 * @param orgID the organization id
 * @param orgData the organization data
 * @param isSubscribed whether the user is subscribed
 * @param fakeOrg a fake org
 */
export const OrganizationOverviewTab = ({
	orgID,
	orgData,
	isSubscribed,
	fakeOrg,
	currentUser,
	currentUserCanEdit,
	isOrgDataDoneLoading,
}) => {
	const [showMorePartners, setShowMorePartners] = useState(false);

	const fakeContact = {
		country: '??',
		email: '??',
		first_name: '??',
		id: 0,
		last_name: '??',
		linked_in_profile: '??',
		organization_id: 0,
		organization_relationship: '??',
		phone_number: '??',
		source: '',
		title: '??',
	};

	const fakeContactsArray = [fakeContact, fakeContact, fakeContact];

	/**
	 * Redirect the user to the add contact page
	 */
	const redirectToAddContact = () => {
		location.assign(`/organizations/${orgID}/contacts/new`);
	};

	/**
	 * Redirect the user to the edit organization page
	 */
	const redirectToEditOrganization = () => {
		if (isSubscribed) {
			location.assign(`/organizations/${orgID}/edit`);
		} else {
			location.assign('/users/sign_up');
		}
	};

	/**
	 * Redirect the user to the edit contact page, for the selected contact
	 * @param index
	 */
	const editContact = (index) => {
		location.assign(`/organizations/${orgID}/contacts/${index}/edit`);
	};

	/**
	 * Deletes the current contact and refreshes the page if done correctly
	 * @param index
	 * @return {Promise<void>}
	 */
	const deleteContact = async (index) => {
		try {
			let response = await ContactsAPI.deleteContact(index);
			if (response.status === 204) {
				location.reload();
			}
		} catch (e) {
			// TODO: Perhaps a visual effect or popup to notify the user something went wrong
			console.error(e);
		}
	};

	/**
	 * Changes the industries array to output a string
	 * @return {string} the formatted string
	 */
	const formatIndustriesArrayToString = () => {
		let returnString = '';
		for (const industry of orgData.industries) {
			returnString += industry.name + ', ';
		}
		if (returnString !== '') {
			returnString = returnString.substring(0, returnString.length - 2);
		}
		return returnString;
	};

	const redirectToSubscribe = () => {
		location.assign('/accounts');
	};

	const formatPhoneNumber = (phoneNumber) => {
		if (!phoneNumber) {
			return '';
		}

		phoneNumber = phoneNumber.replaceAll(/([\s-+()])/g, '');

		let newPhoneNumber = '';
		let offset = phoneNumber.length - 10;

		if (phoneNumber.length > 10) {
			newPhoneNumber = phoneNumber.substr(0, offset) + '-';
		}

		newPhoneNumber +=
			phoneNumber.substr(offset, 3) +
			'-' +
			phoneNumber.substr(offset + 3, 3) +
			'-' +
			phoneNumber.substr(offset + 6, 4);
		return newPhoneNumber;
	};

	if (!isOrgDataDoneLoading) {
		return (
			<div style={{ textAlign: 'center' }}>
				<img src={PurpleLoadingSpinner} alt="Loading spinner" />
			</div>
		);
	}

	return (
		<div className="overview-tab">
			<Row>
				<Column>
					{currentUserCanEdit ? (
						<div
							className="edit-button"
							onClick={redirectToEditOrganization}
						>
							<p>
								Edit Public Profile{' '}
								<i className="fas fa-edit cursor-pointer" />
							</p>
						</div>
					) : (
						''
					)}
					<h6 className="header-font">Sponsorship Overview</h6>
					<p className="body-font description-break">
						{orgData?.description}
					</p>
				</Column>
			</Row>

			<Row>
				<Column
					small={8}
					medium={4}
					className="left-align-spaced-mobile-column"
				>
					{orgData?.address_line_1 ||
					(orgData?.city && orgData?.state) ? (
						<div>
							<h6 className="header-font">Address</h6>
							<div
								className={!currentUser ? 'blur-contents' : ''}
							>
								{orgData.address_line_1 ? (
									<p className="body-font org-address">
										{orgData.address_line_1}
									</p>
								) : (
									''
								)}
								{orgData.city && orgData.state ? (
									<p className="body-font org-address">
										{orgData.city +
											(orgData.state
												? ', ' + orgData.state
												: '')}
									</p>
								) : (
									''
								)}
								{orgData.postal_code ? (
									<p className="body-font org-address">
										{orgData.postal_code}
									</p>
								) : (
									''
								)}
							</div>
						</div>
					) : (
						''
					)}
				</Column>
				<Column
					small={8}
					medium={4}
					className="left-align-spaced-mobile-column"
				>
					{orgData?.industries && orgData?.industries?.length > 0 ? (
						<div>
							<h6 className="header-font">Industries</h6>
							<p className="body-font">
								{formatIndustriesArrayToString()}
							</p>
						</div>
					) : (
						''
					)}
				</Column>
				<Column
					small={8}
					medium={4}
					className="left-align-spaced-mobile-column"
				>
					{orgData?.property_info?.charity_number ? (
						<div>
							<h6 className="header-font">Charity Number</h6>
							<p className="body-font">
								{orgData.property_info.charity_number}
							</p>
						</div>
					) : (
						''
					)}
				</Column>
			</Row>

			<Row>
				<Column>
					{/* Mobile version of the contacts table - If the user is subscribed, then display contacts*/}
					{isSubscribed ? (
						<div className="mobile-contacts-table">
							{orgData?.users_and_contacts?.map(
								(contact, index) => {
									return (
										<div
											className="mobile-contact-container"
											key={index}
										>
											{contact.first_name ||
											contact.last_name ? (
												<Row className="name-and-title-section">
													<p className="body-font">
														<b>
															{contact.first_name +
																' ' +
																contact.last_name}
														</b>
													</p>
													<p className="body-font">
														{contact.title}
													</p>
												</Row>
											) : (
												''
											)}

											{contact.email ? (
												<Row className="info-row">
													<p className="body-font field-key">
														Email
													</p>
													<a
														href={
															'mailto:' +
															contact.email
														}
														target="_blank"
														className="body-font"
													>
														{contact.email}
													</a>
												</Row>
											) : (
												''
											)}

											{contact.phone_number ? (
												<Row className="info-row">
													<p className="body-font field-key">
														Phone
													</p>
													<p className="body-font field-value">
														{formatPhoneNumber(
															contact.phone_number
														)}
													</p>
												</Row>
											) : (
												''
											)}

											{contact.linked_in_profile ? (
												<Row className="info-row">
													<p className="body-font field-key">
														Linkedin
													</p>
													<a
														href={
															contact.linked_in_profile
														}
														target="_blank"
														className="body-font"
													>
														{
															contact.linked_in_profile
														}
													</a>
												</Row>
											) : (
												''
											)}

											{contact.country ? (
												<Row className="info-row">
													<i className="fas fa-map-marker-alt field-key" />
													<p className="body-font field-value">
														{contact.country}
													</p>
												</Row>
											) : (
												''
											)}

											{currentUserCanEdit && (
												<Row>
													<Column small={12}>
														<div
															className="mobile-edit-div"
															onClick={() =>
																editContact(
																	contact.id
																)
															}
														>
															<p className="body-font">
																Edit
															</p>
															<i className="fas fa-edit cursor-pointer" />
														</div>
														<div
															className="mobile-delete-div"
															onClick={() =>
																deleteContact(
																	contact.id
																)
															}
														>
															<p className="body-font">
																Delete
															</p>
															<i className="fas fa-trash-alt cursor-pointer" />
														</div>
													</Column>
												</Row>
											)}
										</div>
									);
								}
							)}
						</div>
					) : (
						// If the user isn't subscribed, then blur out contents and use fake data
						<div className="mobile-contacts-table blur-contents">
							<div className="mobile-contact-container">
								<Row className="name-and-title-section">
									<p className="body-font">??</p>
									<p className="body-font">??</p>
								</Row>

								<Row className="info-row">
									<p className="body-font field-key">Email</p>
									<p className="body-font field-value">??</p>
								</Row>

								<Row className="info-row">
									<p className="body-font field-key">Phone</p>
									<p className="body-font field-value">??</p>
								</Row>

								<Row className="info-row">
									<p className="body-font field-key">
										Linkedin
									</p>
									<p className="body-font field-value">??</p>
								</Row>

								<Row className="info-row">
									<i className="fas fa-map-marker-alt field-key" />
									<p className="body-font field-value">??</p>
								</Row>

								{currentUserCanEdit ? (
									<Row>
										<Column small={12}>
											<div
												className="mobile-edit-div"
												onClick={() =>
													editContact(contact.id)
												}
											>
												<p className="body-font">
													Edit
												</p>
												<i className="fas fa-edit cursor-pointer" />
											</div>
											<div
												className="mobile-delete-div"
												onClick={() =>
													deleteContact(contact.id)
												}
											>
												<p className="body-font">
													Delete
												</p>
												<i className="fas fa-trash-alt cursor-pointer" />
											</div>
										</Column>
									</Row>
								) : (
									''
								)}
							</div>
						</div>
					)}

					<div className="contacts-table">
						<table>
							<thead className="contacts-header">
								<tr>
									<th className="body-font contact-name-title-header">
										Name
									</th>
									<th className="body-font contact-email-header">
										Email
									</th>
									<th className="body-font contact-phone-header">
										Phone
									</th>
									<th className="body-font contact-linkedin-header">
										Linkedin
									</th>
									<th className="body-font contact-country-header">
										<i className="fas fa-map-marker-alt" />
									</th>

									{currentUserCanEdit && (
										<th className="contact-edit-header" />
									)}
									{currentUserCanEdit && (
										<th className="contact-delete-header" />
									)}
								</tr>
							</thead>

							{/*If the user is subscribed, then show contacts*/}
							{isSubscribed ? (
								<tbody>
									{orgData?.users_and_contacts?.map(
										(contact, index) => {
											return (
												<tr key={index}>
													<td className="contact-name-title-header">
														<p
															style={{
																marginBottom:
																	'5px',
															}}
															className="body-font"
														>
															<b>
																{`${contact.first_name} ${contact.last_name}`}
															</b>
														</p>
														<p className="body-font">
															{contact.title}
														</p>
													</td>
													<td className="contact-email-header">
														{contact.email ? (
															<a
																href={
																	'mailto:' +
																	contact.email
																}
															>
																<i className="far fa-envelope" />
															</a>
														) : (
															''
														)}
													</td>
													<td className="contact-phone-header">
														<p className="body-font">
															{contact.phone_number
																? formatPhoneNumber(
																		contact.phone_number
																  )
																: ''}
														</p>
													</td>
													<td className="contact-linkedin-header">
														{contact.linked_in_profile ? (
															<a
																href={
																	contact.linked_in_profile
																}
																target="_blank"
															>
																<i className="fab fa-linkedin" />
															</a>
														) : (
															''
														)}
													</td>
													<td className="contact-country-header">
														<p className="body-font">
															{contact.country}
														</p>
													</td>

													{currentUserCanEdit && (
														<td
															className="contact-edit-header"
															onClick={() =>
																editContact(
																	contact.id
																)
															}
														>
															<i className="fas fa-edit cursor-pointer" />
														</td>
													)}
													{currentUserCanEdit && (
														<td
															className="contact-delete-header"
															onClick={() =>
																deleteContact(
																	contact.id
																)
															}
														>
															<i className="fas fa-trash-alt cursor-pointer" />
														</td>
													)}
												</tr>
											);
										}
									)}
								</tbody>
							) : (
								// If the user isn't subscribed, then blur out the contents
								<tbody className="blur-contents">
									{fakeContactsArray.map((contact, index) => {
										return (
											<tr key={index}>
												<td id="contact-name-title-header">
													<p className="body-font">
														{contact.first_name +
															' ' +
															contact.last_name}
													</p>
													<p className="body-font">
														{contact.title}
													</p>
												</td>
												<td className="contact-email-header">
													{contact.email ? (
														<a
															href={
																'mailto:' +
																contact.email
															}
														>
															<i className="far fa-envelope" />
														</a>
													) : (
														''
													)}
												</td>
												<td className="contact-phone-header">
													<p className="body-font">
														{contact.phone_number
															? formatPhoneNumber(
																	contact.phone_number
															  )
															: ''}
													</p>
												</td>
												<td className="contact-linkedin-header">
													{contact.linked_in_profile ? (
														<a
															href={
																contact.linked_in_profile
															}
															target="_blank"
														>
															<i className="fab fa-linkedin" />
														</a>
													) : (
														''
													)}
												</td>
												<td className="contact-country-header">
													<p className="body-font">
														{contact.country}
													</p>
												</td>

												{currentUserCanEdit ? (
													<td
														className="contact-edit-header"
														onClick={() =>
															editContact(
																contact.id
															)
														}
													>
														<i className="fas fa-edit cursor-pointer" />
													</td>
												) : (
													<></>
												)}
												{currentUserCanEdit ? (
													<td
														className="contact-delete-header"
														onClick={() =>
															deleteContact(
																contact.id
															)
														}
													>
														<i className="fas fa-trash-alt cursor-pointer" />
													</td>
												) : (
													<></>
												)}
											</tr>
										);
									})}
								</tbody>
							)}
						</table>
						{/*If the user has editing privileges, then show the add contact button*/}
						{currentUserCanEdit ? (
							<div
								className="primary-button float-right"
								onClick={redirectToAddContact}
							>
								<p>Add Contact</p>
							</div>
						) : (
							<div
								className="primary-button float-right blur-contents"
								onClick={redirectToAddContact}
							>
								<p>Add Contact</p>
							</div>
						)}
					</div>
				</Column>
			</Row>

			<Row>
				<Column>
					<div>
						{/* If the user is subscribed and there are partners to show, show the first 5 */}
						{/* TODO: MAKE REDERING DYNAMIC */}
						{isSubscribed ? (
							orgData?.partners?.length > 0 ? (
								<div>
									<h6 className="header-font top-10px-margin">
										Sponsorship partners
									</h6>
									<div className="sponsorship-partners-container-wrapper">
										<div className="sponsorship-partners-container">
											{orgData?.partners
												?.slice(0, 5)
												.map((partner) => (
													<SponsorshipPartner
														orgData={partner}
													/>
												))}
										</div>
										{showMorePartners && (
											<div className="sponsorship-partners-container">
												{orgData?.partners
													?.slice(
														5,
														orgData?.partners
															.length - 1
													)
													.map((partner) => (
														<SponsorshipPartner
															orgData={partner}
														/>
													))}
											</div>
										)}
									</div>

									{orgData?.partners?.length > 5 ? (
										<div className="center-button-container">
											<div
												className="primary-button center"
												onClick={() =>
													setShowMorePartners(
														!showMorePartners
													)
												}
											>
												<p>
													{showMorePartners
														? 'View Fewer Partners'
														: 'View More Partners'}
												</p>
											</div>
										</div>
									) : (
										''
									)}
								</div>
							) : (
								''
							)
						) : (
							// If the user isn't subscribed, then show fake organizations
							<div>
								<h6 className="header-font top-10px-margin">
									Sponsorship partners
								</h6>
								<div
									className={
										!currentUser ? 'blur-contents' : ''
									}
								>
									<div className="sponsorship-partners-container blur-contents">
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
									</div>
								</div>

								{showMorePartners ? (
									<div className="sponsorship-partners-container">
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
										<SponsorshipPartner
											className="blur-contents"
											orgData={fakeOrg}
										/>
									</div>
								) : (
									''
								)}
								<div className="center-button-container">
									<div
										className="primary-button center blur-contents"
										onClick={() =>
											setShowMorePartners(
												!showMorePartners
											)
										}
									>
										<p>
											{showMorePartners
												? 'View Fewer Partners'
												: 'View More Partners'}
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</Column>
			</Row>

			{/*Subscribe popup over the contacts*/}
			{!isSubscribed && (
				<div className="subscribe-container">
					<h5>
						Subscribe now for exclusive content and recommendations
					</h5>
					<a href="/accounts">
						<div class="purchase-button">
							<p>SUBSCRIBE NOW</p>
						</div>
					</a>
				</div>
			)}
		</div>
	);
};
